<template>
    <IFramePresentation
        v-model:user-parameter="userParameter"
        v-model:show-event="displaySingleEvent"
        :i-frame-settings="iFrameSettings"
        :available-filters="availableFilters"
        :loading="loadingIFrameSettings || loadingMoreEvents || loadingAvailableFilters"
        :per-page="meta ? meta.per_page : 10"
        :events="events"
        :event-overview="eventOverview"
        :exception-while-loading-events="exceptionWhileLoading"
        :message-source-random-id="messageSourceRandomId"
        :paginate-events="paginateEvents"
        :current-page="currentPage"
        :last-page="meta ? meta.last_page : 1"
        :parent-url="parentUrl"
        @load-next-page="loadNextPageIfAvailable"
        @load-specific-page="loadSpecificPageIfAvailable"
        @show-event="showEvent"
        @show-main-page="resetToMainPage" />
</template>

<script>
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import formatIFrameParameterMixin from "@/mixins/eventIFrame/formatIFrameParameterMixin";

export default {
    name: "IFrameDataLoader",
    mixins: [formatIFrameParameterMixin],
    props: {
        id: {
            type: [Number, String]
        },
        token: {
            type: String
        },
        messageSourceRandomId: {
            type: String
        }
    },
    data() {
        return {
            events: [],
            eventOverview: [],
            meta: null,
            iFrameSettings: null,
            availableFilters: {},
            userParameter: {
                id: null,
                offerNumber: null,
                offerNumberOverwrite: null,
                name: null,
                teaser: null,
                dateFrom: null,
                dateTo: null
            },
            previousFetchedUserParameters: {
                id: null,
                offerNumber: null,
                offerNumberOverwrite: null,
                name: null,
                teaser: null,
                dateFrom: null,
                dateTo: null
            },
            currentPage: 0,
            exceptionWhileLoading: false,
            loadingIFrameSettings: false,
            loadingEventOverview: false,
            loadingAvailableFilters: false,
            loadingMoreEvents: false,
            displaySingleEvent: {id: null, offerNumber: null},
            lockUserParameters: false,
            parentUrl: null
        }
    },
    computed: {
        onlyInfiniteScrollDisplayTypes() {
            return ['list', 'tile'].includes(this.iFrameSettings?.display?.type)
                && !this.iFrameSettings?.display?.allowedTypes?.includes('map')
                && !this.iFrameSettings?.display?.allowedTypes?.includes('date')
                && !this.iFrameSettings?.display?.allowedTypes?.includes('compact')
        },
        embedStructuredData() {
            return !!this.iFrameSettings?.features?.structuredData && this.parentUrl
        },
        integerIdForQueryingBackend() {
            return parseInt(this.id) ?? null
        },
        paginateEvents() {
            return this.iFrameSettings?.display?.paginate ?? false
        },
        paginatedDisplayType() {
            return this.iFrameSettings?.display?.type === 'list' || this.iFrameSettings?.display?.type === 'tile'
        },
    },
    methods: {
        fetch() {
            if (!this.id || !this.token) return
            this.loadIFrameSettingsAndInitialEventOverview().then(() => {
                if (this.paginateEvents)
                    this.loadSpecificPageIfAvailable(1)
                else
                    this.loadNextPageIfAvailable()
            })
            this.loadAvailableFilters()
        },
        loadWithNewUserParameters(loadEventOverview = true) {
            if (isEqual(this.previousFetchedUserParameters, this.userParameter)) return
            this.exceptionWhileLoading = false
            this.previousFetchedUserParameters = cloneDeep(this.userParameter)
            if (loadEventOverview) this.loadEventOverview()
            this.currentPage = 1;
            this.sendCurrentPageLoadRequest()

        },
        async loadIFrameSettingsAndInitialEventOverview() {
            this.loadingIFrameSettings = true
            try {
                const response = await this.$axios.get(this.$apiRoute('api.iFrames.showIframeSettings', {iFrame: this.integerIdForQueryingBackend}))
                this.iFrameSettings = Array.isArray(response.data) ? {} : response.data
            } catch (e) {
                this.exceptionWhileLoading = true
            } finally {
                this.loadingIFrameSettings = false
            }
            // needs to be loaded after iframe settings, to avoid redundant loading of event overview in list/tile display
            await this.loadEventOverview(true)
        },
        async loadEventOverview(initial = false) {
            if (this.loadingEventOverview || (this.onlyInfiniteScrollDisplayTypes && !this.embedStructuredData)) return
            this.loadingEventOverview = true
            try {
                const response = await this.$axios.get(this.$apiRoute('api.iFrames.eventOverview', {iFrame: this.integerIdForQueryingBackend}),
                                                       {params: initial ? {token: this.token} : {token: this.token, ...this.formattedUserParameters}}
                )
                this.eventOverview = response.data.data
            } catch (e) {
                this.exceptionWhileLoading = true
            } finally {
                this.loadingEventOverview = false
            }
        },
        loadNextPageIfAvailable() {
            if (!this.meta || this.meta.last_page > this.currentPage) {
                this.loadingMoreEvents = true
                this.debouncedSendNextPageLoadRequest()
            }
        },
        loadSpecificPageIfAvailable(page = 1) {
            if (!this.meta || this.meta.last_page >= page) {
                this.loadingMoreEvents = true
                this.currentPage = page
                this.debouncedSendSpecificPageLoadRequest()
            }
        },
        async loadAvailableFilters() {
            this.loadingAvailableFilters = true
            try {
                const response = await this.$axios.get(this.$apiRoute('api.iFrames.availableFilterValues', {iFrame: this.integerIdForQueryingBackend}),
                                                       {params: {token: this.token}})
                this.availableFilters = response.data
            } catch (e) {
                this.exceptionWhileLoading = true
            } finally {
                this.loadingAvailableFilters = false
            }
        },
        sendNextPageLoadRequest() {
            this.currentPage += 1
            this.sendLoadRequest()
        },
        sendCurrentPageLoadRequest() {
            this.events = []
            this.sendLoadRequest()
        },
        async sendLoadRequest() {
            this.loadingMoreEvents = true
            try {
                const response = await this.$axios.get(this.$apiRoute('api.iFrames.indexEvents', {iFrame: this.integerIdForQueryingBackend}),
                                                       {params: {token: this.token, page: this.currentPage, ...this.formattedUserParameters}}
                )
                this.events = this.events.concat(response.data.data)
                this.meta = response.data.meta
            } catch (e) {
                this.exceptionWhileLoading = true
            } finally {
                this.loadingMoreEvents = false
                this.releaseUserParameterLoadTrigger()
                this.scrollNewPageToTopIfNecessary()
            }
        },
        showEvent(eventData) {
            if (eventData.offerNumber && !this.events?.some(event => event.offerNumber === eventData.offerNumber)) {
                this.lockUserParameterLoadTrigger()
                this.userParameter = {...this.userParameter, offerNumberOverwrite: eventData.offerNumber}
                this.loadWithNewUserParameters(false)
            } else if (eventData.id && !this.events?.some(event => event.id == eventData.id)) {
                this.lockUserParameterLoadTrigger()
                this.userParameter = {...this.userParameter, id: eventData.id}
                this.loadWithNewUserParameters(false)
            }
            this.displaySingleEvent = {...this.displaySingleEvent, ...eventData}
        },
        resetToMainPage() {
            this.displaySingleEvent = {id: null, offerNumber: null}
            this.userParameter = {...this.userParameter, offerNumberOverwrite: null, id: null}
            this.loadWithNewUserParameters(false)
        },
        lockUserParameterLoadTrigger() {
            this.lockUserParameters = true
        },
        releaseUserParameterLoadTrigger() {
            this.lockUserParameters = false
        },
        scrollNewPageToTopIfNecessary() {
            if(!(this.paginateEvents && this.paginatedDisplayType) || this.currentPage === 1) return
            // autoscroll to top pagination
            const elementId = "upper-pagination";
            const element = document.getElementById(elementId);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        }
    },
    mounted() {
        this.fetch()
    },
    created() {
        this.debouncedSendNextPageLoadRequest = debounce(function () {
            this.sendNextPageLoadRequest()
        }, 50)
        this.debouncedSendSpecificPageLoadRequest = debounce(function () {
            this.sendCurrentPageLoadRequest()
        }, 50)
        this.debouncedLoadWithNewUserParameters = debounce(function () {
            this.loadWithNewUserParameters()
        }, 1000)
        if (this.$route.query.parentUrl) {
            this.parentUrl = this.$route.query.parentUrl
        }
        if (this.$route.query.offerNumber) {
            this.showEvent({id: null, offerNumber: this.$route.query.offerNumber})
        } else if (this.$route.query.eventId) {
            this.showEvent({id: this.$route.query.eventId, offerNumber: null})
        }
    },
    watch: {
        userParameter() {
            if (!this.lockUserParameters)
                this.debouncedLoadWithNewUserParameters()
        },
        iFrameSettings(value) {
            if (value && !value.features?.structuredData) this.$emit('index', false)
        }
    }
}
</script>

<style>
</style>
